/* eslint-disable react/jsx-props-no-spreading */
import { KonvaEventObject } from 'konva/lib/Node';
import React, { FC, memo, ReactNode, useCallback } from 'react';
import { Ellipse, Group, Line, Path, Rect, Text } from 'react-konva';
import { PIXELS_PER_METER } from '../model/constants/mapScale';
import { Shape, ShapeType, WarehouseMapTool } from '../model/types';
import { getPolygonArea, getPolygonBounds, getRectArea } from '../utils/helpers/shapeGeometry';

// Ant Design LockOutlined SVG path data (viewBox "64 64 896 896").
// Using Konva Path so the icon renders on canvas without any DOM involvement.
const LOCK_PATH =
  'M832 464h-68V240c0-70.7-57.3-128-128-128H388c-70.7 0-128 57.3-128 128v224h-68c-17.7 0-32 14.3-32 32v384c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V496c0-17.7-14.3-32-32-32zM540 701v67c0 4.4-3.6 8-8 8h-40c-4.4 0-8-3.6-8-8v-67a48.01 48.01 0 1 1 56 0zm152-237H332V240c0-30.9 25.1-56 56-56h248c30.9 0 56 25.1 56 56v224z';
// The viewBox spans 896 units starting at offset 64.
const LOCK_ICON_PX = 14;
const LOCK_ICON_SCALE = LOCK_ICON_PX / 896;
const LOCK_ICON_VIEWBOX_OFFSET = 64;

interface WarehouseMapShapesProps {
  activeTool: WarehouseMapTool;
  shapes: Shape[];
  floorLabelFontSize: number;
  sizeCodeLabelsById?: Record<string, string>;
  onDragStart: (e: KonvaEventObject<MouseEvent>) => void;
  onDragMove: (e: KonvaEventObject<MouseEvent>) => void;
  onDragEnd: (e: KonvaEventObject<MouseEvent>) => void;
  onTransformEnd: (e: KonvaEventObject<MouseEvent>) => void;
  /** When set, the label of this shape becomes draggable. */
  labelEditShapeId?: Nullable<string>;
  onLabelDragEnd?: (shapeId: string, offsetX: number, offsetY: number) => void;
}

export const WarehouseMapShapes: FC<WarehouseMapShapesProps> = memo((props) => {
  const {
    shapes,
    activeTool,
    floorLabelFontSize,
    sizeCodeLabelsById,
    onDragStart,
    onDragMove,
    onDragEnd,
    onTransformEnd,
    labelEditShapeId,
    onLabelDragEnd,
  } = props;

  const shapeCursor = activeTool === WarehouseMapTool.POINTER ? 'pointer' : 'default';
  const isPointerActive = activeTool === WarehouseMapTool.POINTER;

  const commonOptions = {
    cursor: shapeCursor,
    onDragStart,
    onDragMove,
    onDragEnd,
    onTransformEnd,
  };

  const getDraggable = (shape: Shape): boolean => isPointerActive && !shape.locked;

  const buildConstructorBoxText = useCallback(
    (label: string, widthMeters: number, heightMeters: number, actualAreaSquareMeters: number, sizeCodeId?: string): string => {
      const sizeCodeLabel = sizeCodeId ? sizeCodeLabelsById?.[sizeCodeId] : undefined;
      return [
        label,
        sizeCodeLabel ?? '',
        // `${roundNumber(heightMeters)}m x ${roundNumber(widthMeters)}m (${roundNumber(actualAreaSquareMeters)} m2)`,
      ].join('\n');
    },
    [sizeCodeLabelsById],
  );

  const getShapes = (): Nullable<ReactNode> => {
    return shapes.map((shape) => {
      const fontSize = 'fontSize' in shape ? shape.fontSize ?? floorLabelFontSize : floorLabelFontSize;

      const boxText =
        'label' in shape && 'width' in shape
          ? buildConstructorBoxText(
              shape.label,
              shape.width / PIXELS_PER_METER,
              shape.height / PIXELS_PER_METER,
              getRectArea(shape.width, shape.height, PIXELS_PER_METER),
              shape.sizeCodeId,
            )
          : '';

      const polygonBoxText =
        'label' in shape && 'points' in shape
          ? buildConstructorBoxText(
              shape.label,
              getPolygonBounds(shape.points).width / PIXELS_PER_METER,
              getPolygonBounds(shape.points).height / PIXELS_PER_METER,
              getPolygonArea(shape.points, PIXELS_PER_METER),
              shape.sizeCodeId,
            )
          : '';

      switch (shape.type) {
        case ShapeType.RECTANGLE: {
          const isRectLabelEdit = labelEditShapeId === shape.id;
          const rectLabelOffsetX: number = shape.labelOffsetX ?? 0;
          const rectLabelOffsetY: number = shape.labelOffsetY ?? 0;
          return (
            <Group
              key={shape.id}
              name="shape"
              id={shape.id}
              x={shape.x}
              y={shape.y}
              scaleX={shape.scaleX}
              scaleY={shape.scaleY}
              width={shape.width}
              height={shape.height}
              rotation={shape.rotation}
              draggable={getDraggable(shape)}
              {...commonOptions}
            >
              <Rect
                id={shape.id}
                width={shape.width}
                height={shape.height}
                fill={shape.fill}
                stroke={shape.stroke}
                strokeWidth={shape.strokeWidth}
                cornerRadius={shape.cornerRadius}
                strokeScaleEnabled={false}
              />
              {shape.locked && (
                <Path
                  data={LOCK_PATH}
                  fill="#788593"
                  x={(shape.width - LOCK_ICON_PX) / 2}
                  y={6}
                  scaleX={LOCK_ICON_SCALE}
                  scaleY={LOCK_ICON_SCALE}
                  offsetX={LOCK_ICON_VIEWBOX_OFFSET}
                  offsetY={LOCK_ICON_VIEWBOX_OFFSET}
                  listening={false}
                  perfectDrawEnabled={false}
                />
              )}
              {isRectLabelEdit && (
                <Rect
                  x={rectLabelOffsetX + shape.width / 2}
                  y={rectLabelOffsetY + shape.height / 2}
                  width={shape.width}
                  height={shape.height}
                  offsetX={shape.width / 2}
                  offsetY={shape.height / 2}
                  rotation={shape.labelRotation ?? 0}
                  stroke="#1677ff"
                  strokeWidth={1}
                  dash={[6, 4]}
                  fill="transparent"
                  listening={false}
                  strokeScaleEnabled={false}
                />
              )}
              <Text
                name={isRectLabelEdit ? 'label-handle' : undefined}
                text={boxText}
                x={rectLabelOffsetX + shape.width / 2}
                y={rectLabelOffsetY + shape.height / 2}
                width={shape.width}
                height={shape.height}
                offsetX={shape.width / 2}
                offsetY={shape.height / 2}
                rotation={shape.labelRotation ?? 0}
                fontSize={fontSize}
                align="center"
                verticalAlign="middle"
                listening={isRectLabelEdit}
                draggable={isRectLabelEdit}
                cursor={isRectLabelEdit ? 'move' : 'default'}
                onDragStart={(e) => {
                  e.cancelBubble = true;
                }}
                onDragEnd={(e) => {
                  e.cancelBubble = true;
                  onLabelDragEnd?.(shape.id, e.target.x() - shape.width / 2, e.target.y() - shape.height / 2);
                }}
              />
            </Group>
          );
        }
        case ShapeType.CIRCLE:
          return (
            <Group
              key={shape.id}
              name="shape"
              id={shape.id}
              x={shape.x}
              y={shape.y}
              scaleX={shape.scaleX}
              scaleY={shape.scaleY}
              rotation={shape.rotation}
              draggable={getDraggable(shape)}
              {...commonOptions}
            >
              <Ellipse
                key={shape.id}
                id={shape.id}
                fill={shape.fill}
                stroke={shape.stroke}
                radiusX={shape.radiusX}
                radiusY={shape.radiusY}
                strokeWidth={shape.strokeWidth}
                strokeScaleEnabled={false}
                width={shape.radiusX * 2}
                height={shape.radiusY * 2}
              />
              {shape.locked && (
                <Path
                  data={LOCK_PATH}
                  fill="#788593"
                  x={-LOCK_ICON_PX / 2}
                  y={-shape.radiusY + 6}
                  scaleX={LOCK_ICON_SCALE}
                  scaleY={LOCK_ICON_SCALE}
                  offsetX={LOCK_ICON_VIEWBOX_OFFSET}
                  offsetY={LOCK_ICON_VIEWBOX_OFFSET}
                  listening={false}
                  perfectDrawEnabled={false}
                />
              )}
              <Text
                text={shape.label}
                x={0}
                y={0}
                width={shape.radiusX * 2}
                height={shape.radiusY * 2}
                offsetX={shape.radiusX}
                offsetY={shape.radiusY}
                rotation={shape.labelRotation ?? 0}
                fontSize={fontSize}
                align="center"
                verticalAlign="middle"
                listening={false}
              />
            </Group>
          );
        case ShapeType.LINE:
          return <Line key={shape.id} name="shape" {...shape} {...commonOptions} draggable={getDraggable(shape)} x={0} y={0} />;
        case ShapeType.TEXT:
          return (
            <Text
              key={shape.id}
              name="shape"
              {...shape}
              text={shape.label}
              fontSize={fontSize}
              strokeWidth={1}
              {...commonOptions}
              draggable={getDraggable(shape)}
            />
          );
        case ShapeType.POLYGON:
          return (
            <Line
              key={shape.id}
              name="shape"
              {...shape}
              {...commonOptions}
              draggable={getDraggable(shape)}
              x={0}
              y={0}
              closed
              fillEnabled={false}
              hitStrokeWidth={Math.max(12, shape.strokeWidth)}
            />
          );
        case ShapeType.POLYGON_BOX: {
          const polygonBoxBounds = getPolygonBounds(shape.points);
          const polyLabelOffsetX: number = shape.labelOffsetX ?? 0;
          const polyLabelOffsetY: number = shape.labelOffsetY ?? 0;
          const polyLabelX: number = polygonBoxBounds.minX + polyLabelOffsetX;
          const polyLabelY: number = polygonBoxBounds.minY + polyLabelOffsetY;
          const isPolyLabelEdit = labelEditShapeId === shape.id;
          return (
            <Group key={shape.id} name="shape" id={shape.id} rotation={shape.rotation} {...commonOptions} draggable={getDraggable(shape)}>
              <Line
                key={shape.id}
                name="shape"
                points={shape.points}
                fill={shape.fill}
                stroke={shape.stroke}
                strokeWidth={shape.strokeWidth}
                scaleX={shape.scaleX}
                scaleY={shape.scaleY}
                x={0}
                y={0}
                closed
              />
              {shape.locked && (
                <Path
                  data={LOCK_PATH}
                  fill="#788593"
                  x={polygonBoxBounds.minX + (polygonBoxBounds.width - LOCK_ICON_PX) / 2}
                  y={polygonBoxBounds.minY + 6}
                  scaleX={LOCK_ICON_SCALE}
                  scaleY={LOCK_ICON_SCALE}
                  offsetX={LOCK_ICON_VIEWBOX_OFFSET}
                  offsetY={LOCK_ICON_VIEWBOX_OFFSET}
                  listening={false}
                  perfectDrawEnabled={false}
                />
              )}
              {isPolyLabelEdit && (
                <Rect
                  x={polyLabelX + polygonBoxBounds.width / 2}
                  y={polyLabelY + polygonBoxBounds.height / 2}
                  width={polygonBoxBounds.width}
                  height={polygonBoxBounds.height}
                  offsetX={polygonBoxBounds.width / 2}
                  offsetY={polygonBoxBounds.height / 2}
                  rotation={shape.labelRotation ?? 0}
                  stroke="#1677ff"
                  strokeWidth={1}
                  dash={[6, 4]}
                  fill="transparent"
                  listening={false}
                  strokeScaleEnabled={false}
                />
              )}
              <Text
                name={isPolyLabelEdit ? 'label-handle' : undefined}
                text={polygonBoxText}
                x={polyLabelX + polygonBoxBounds.width / 2}
                y={polyLabelY + polygonBoxBounds.height / 2}
                width={polygonBoxBounds.width}
                height={polygonBoxBounds.height}
                offsetX={polygonBoxBounds.width / 2}
                offsetY={polygonBoxBounds.height / 2}
                rotation={shape.labelRotation ?? 0}
                fontSize={fontSize}
                align="center"
                verticalAlign="middle"
                listening={isPolyLabelEdit}
                draggable={isPolyLabelEdit}
                cursor={isPolyLabelEdit ? 'move' : 'default'}
                onDragStart={(e) => {
                  e.cancelBubble = true;
                }}
                onDragEnd={(e) => {
                  e.cancelBubble = true;
                  const newOffsetX: number = e.target.x() - polygonBoxBounds.minX - polygonBoxBounds.width / 2;
                  const newOffsetY: number = e.target.y() - polygonBoxBounds.minY - polygonBoxBounds.height / 2;
                  onLabelDragEnd?.(shape.id, newOffsetX, newOffsetY);
                }}
              />
            </Group>
          );
        }
        default:
          return null;
      }
    });
  };

  return <>{getShapes()}</>;
});
